import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useMarina } from "src/contexts/MarinaContext"

import DataTableV2 from "src/components/DataTableV2"

import {
  fetchContactInvoicesForPrint,
  getContactInvoices,
} from "src/api/Billing/Payments"

import useDatatable from "src/hooks/use_datatable"
import { useTracker } from "src/hooks/use_tracker"

import ContactBalances, { initialLedgerDataProps } from "./ContactBalances"
import ContactInvoicesFilters from "./ContactInvoicesFilters"
import ContactInvoicesPrintView from "./ContactInvoicesPrintView"
import { buildColumns } from "./columns"
import { calculateSummaryTotals } from "./helpers"

const STATUS_LABELS = {
  past_due: "Overdue",
  failed: "Failed",
  settled: "Settled",
  processing: "Processing",
  disputed: "Disputed",
  scheduled: "Scheduled",
}

const ContactInvoicesPanel = ({
  contactId,
  initialLedgerData,
  contact,
  marina,
}) => {
  const tracker = useTracker()
  const { marinaSlug, presentmentCurrency } = useMarina()
  const [printData, setPrintData] = useState(null)

  const table = useDatatable({
    queryKey: ["contactInvoices", contactId],
    fetchFn: (params) =>
      getContactInvoices({ contactId, marinaSlug, ...params }),
    defaultFilters: {
      statuses: [],
      due_date_lteq: "",
      due_date_gteq: "",
    },
  })

  useEffect(() => {
    if (printData) window.print()
  }, [printData])

  const handleExportSummary = async () => {
    tracker.trackEvent("customer_invoices_tab:export_summary_pressed")
    const response = await fetchContactInvoicesForPrint({
      contactId,
      marinaSlug,
      filters: {
        statuses: table.filters.statuses,
        due_date_gteq: table.filters.due_date_gteq,
        due_date_lteq: table.filters.due_date_lteq,
      },
    })
    const invoices = response.data || []
    setPrintData({
      invoices,
      summaryTotals: calculateSummaryTotals(invoices),
      startDate: table.filters.due_date_gteq
        ? parseISO(table.filters.due_date_gteq)
        : null,
      endDate: table.filters.due_date_lteq
        ? parseISO(table.filters.due_date_lteq)
        : null,
      statusFilters: (table.filters.statuses || []).map(
        (s) => STATUS_LABELS[s] || s
      ),
    })
  }

  const rowConfig = useMemo(
    () => ({
      getViewUrl: (invoice) =>
        `/manage/${marinaSlug}/payments/${invoice.id}/view`,
      onViewClick: () =>
        tracker.trackEvent("customer_invoices_tab:view_invoice_pressed"),
    }),
    [marinaSlug, tracker]
  )

  const actions = useMemo(() => {
    const openSource = (invoice) => {
      tracker.trackEvent("customer_invoices_tab:view_source_pressed")
      window.open(invoice.sourcePath, "_blank")
    }

    return [
      {
        label: "View Reservation",
        icon: "calendar-days",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Reservation",
        onClickSingle: openSource,
      },
      {
        label: "View Contract",
        icon: "file-contract text-sm",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Contract",
        onClickSingle: openSource,
      },
      {
        label: "View Sale",
        icon: "money-bills",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Sale",
        onClickSingle: openSource,
      },
    ]
  }, [tracker])

  return (
    <>
      <div
        data-testid="contact-invoices-panel"
        className="flex flex-col space-y-4 px-4 print:hidden"
      >
        <ContactBalances initialLedgerData={initialLedgerData} />
        <ContactInvoicesFilters
          table={table}
          onExportSummary={handleExportSummary}
        />
        <DataTableV2
          name="contact-invoices"
          columns={buildColumns({ presentmentCurrency })}
          data={table.records}
          rowConfig={rowConfig}
          actions={actions}
          maxHeight="calc(100vh - 150px)"
          {...table.tableProps}
        />
      </div>
      {printData ? (
        <ContactInvoicesPrintView
          invoices={printData.invoices}
          contact={contact}
          marina={marina}
          summaryTotals={printData.summaryTotals}
          presentmentCurrency={presentmentCurrency}
          startDate={printData.startDate}
          endDate={printData.endDate}
          statusFilters={printData.statusFilters}
        />
      ) : null}
    </>
  )
}

ContactInvoicesPanel.propTypes = {
  contactId: PropTypes.string.isRequired,
  initialLedgerData: PropTypes.shape(initialLedgerDataProps).isRequired,
  contact: PropTypes.object.isRequired,
  marina: PropTypes.object.isRequired,
}

export default ContactInvoicesPanel
